import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { api } from '../lib/api';
import { useI18n } from '../lib/i18n';
import { formatDays } from '../lib/format';
import { FormPage } from '../components/FormPage';
import { Field, Textarea, useToast } from '../components/ui';
import { PenaltyWhat } from './Violations';

const BACK = '/violations';

/**
 * Cancelling a penalty (§13.4), objecting to one's own penalty, an
 * administrator approving or rejecting that objection, and voiding a violation
 * (§11.6). All of them take a reason, so all of them are pages rather than popups.
 *
 * Routes: `/violations/penalties/:id/cancel`, `/violations/penalties/:id/object`,
 * `/violations/penalties/:id/objection/approve`, `…/objection/reject` and
 * `/violations/:id/void`.
 */
export function CancelPenaltyPage() {
  const { id = '' } = useParams();
  const { t } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [reason, setReason] = useState('');

  const cancel = useMutation({
    mutationFn: () => api.post(`/penalties/${id}/cancel`, { reason }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['penalties'] });
      // Back to the list or the penalty's page, whichever it came from.
      if (location.key !== 'default') navigate(-1);
      else navigate(`/violations/penalties/${id}`, { replace: true });
    },
    onError: (e: any) => toast.error(e.message),
  });

  return (
    <FormPage
      title={t('violations.cancel')}
      subtitle={t('violations.cancelHint')}
      backLabel={t('common.back')}
      submitLabel={t('common.confirm')}
      destructive
      submitting={cancel.isPending}
      disabled={!reason.trim()}
      onSubmit={() => cancel.mutate()}
    >
      <Field label={t('common.reason')} required>
        <Textarea value={reason} onChange={(e) => setReason(e.target.value)} autoFocus />
      </Field>
    </FormPage>
  );
}

/** Whose penalty, what it is for and its days — above the reason on these forms. */
function PenaltySummary({ penalty, objection = false }: { penalty: any; objection?: boolean }) {
  const { t, locale, localized } = useI18n();
  return (
    <dl className="grid gap-3 text-body-sm sm:grid-cols-[1fr_2fr_auto]">
      <div>
        <dt className="text-caption text-muted">{t('common.employee')}</dt>
        <dd className="text-[var(--text-primary)]">{localized(penalty.user)}</dd>
      </div>
      <div className="min-w-0">
        <dt className="text-caption text-muted">{t('common.reason')}</dt>
        <dd className="text-[var(--text-primary)]">
          <PenaltyWhat penalty={penalty} />
        </dd>
      </div>
      <div>
        <dt className="text-caption text-muted">{t('violations.days')}</dt>
        <dd className="whitespace-nowrap tabular text-error-fg dark:text-error-dark">
          {penalty.days != null ? formatDays(penalty.days, locale) : '—'}
        </dd>
      </div>
      {objection && penalty.objection && (
        <div className="sm:col-span-3">
          <dt className="text-caption text-muted">{t('violations.objectionFrom', { name: localized(penalty.user) })}</dt>
          <dd className="whitespace-pre-wrap text-[var(--text-primary)]" dir="auto">
            {penalty.objection.reason}
          </dd>
        </div>
      )}
    </dl>
  );
}

/**
 * The employee objects to a penalty charged to them, with a reason (operator,
 * 2026-10-22: "let any employee add Objection … with reason"). Once per penalty,
 * while payroll has not taken it; an administrator approves or rejects it.
 */
export function ObjectPenaltyPage() {
  const { id = '' } = useParams();
  const { t } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [reason, setReason] = useState('');

  const penalty = useQuery({
    queryKey: ['penalties', 'one', id],
    queryFn: () => api.get<any>(`/penalties/${id}`),
  });

  const send = useMutation({
    mutationFn: () => api.post(`/penalties/${id}/objection`, { reason }),
    onSuccess: () => {
      toast.success(t('violations.objectSent'));
      void queryClient.invalidateQueries({ queryKey: ['penalties'] });
      // The penalty's page, where the objection now shows.
      navigate(`/violations/penalties/${id}`, { replace: true });
    },
    onError: (e: any) => toast.error(e.message),
  });

  const data = penalty.data;
  return (
    <FormPage
      title={t('violations.objectTitle')}
      subtitle={t('violations.objectHint')}
      backLabel={t('common.back')}
      submitLabel={t('violations.object')}
      submitting={send.isPending}
      disabled={!reason.trim() || !data?.can?.object}
      loading={penalty.isLoading}
      error={penalty.error}
      onRetry={() => void penalty.refetch()}
      summary={data && <PenaltySummary penalty={data} />}
      onSubmit={() => send.mutate()}
    >
      <Field label={t('violations.objectReason')} required>
        <Textarea value={reason} maxLength={1000} onChange={(e) => setReason(e.target.value)} autoFocus />
      </Field>
    </FormPage>
  );
}

/**
 * An administrator approves the objection — the penalty is cancelled — or
 * rejects it — the penalty stands — with a reason the employee reads.
 */
export function DecideObjectionPage({ decision }: { decision: 'approve' | 'reject' }) {
  const { id = '' } = useParams();
  const { t } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [reason, setReason] = useState('');
  const approve = decision === 'approve';

  const penalty = useQuery({
    queryKey: ['penalties', 'one', id],
    queryFn: () => api.get<any>(`/penalties/${id}`),
  });

  const decide = useMutation({
    mutationFn: () => api.post(`/penalties/${id}/objection/${decision}`, { reason }),
    onSuccess: () => {
      toast.success(t(approve ? 'violations.objectionApprovedToast' : 'violations.objectionRejectedToast'));
      void queryClient.invalidateQueries({ queryKey: ['penalties'] });
      // Back to the review list or the penalty's page, whichever it came from.
      if (location.key !== 'default') navigate(-1);
      else navigate(`/violations/penalties/${id}`, { replace: true });
    },
    onError: (e: any) => toast.error(e.message),
  });

  const data = penalty.data;
  return (
    <FormPage
      title={t(approve ? 'violations.approveObjectionTitle' : 'violations.rejectObjectionTitle')}
      subtitle={t(approve ? 'violations.approveObjectionHint' : 'violations.rejectObjectionHint')}
      backLabel={t('common.back')}
      submitLabel={t(approve ? 'violations.approveObjection' : 'violations.rejectObjection')}
      destructive={!approve}
      submitting={decide.isPending}
      disabled={!reason.trim() || !data?.can?.decideObjection}
      loading={penalty.isLoading}
      error={penalty.error}
      onRetry={() => void penalty.refetch()}
      summary={data && <PenaltySummary penalty={data} objection />}
      onSubmit={() => decide.mutate()}
    >
      <Field label={t('violations.decisionReason')} required>
        <Textarea value={reason} maxLength={1000} onChange={(e) => setReason(e.target.value)} autoFocus />
      </Field>
    </FormPage>
  );
}

export function VoidViolationPage() {
  const { id = '' } = useParams();
  const { t } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [reason, setReason] = useState('');

  const voidIt = useMutation({
    mutationFn: () => api.post(`/tickets/violations/${id}/void`, { reason }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['violations'] });
      void queryClient.invalidateQueries({ queryKey: ['penalties'] });
      navigate(BACK);
    },
    onError: (e: any) => toast.error(e.message),
  });

  return (
    <FormPage
      title={t('violations.void')}
      subtitle={t('violations.voidHint')}
      backTo={BACK}
      backLabel={t('violations.title')}
      submitLabel={t('common.confirm')}
      destructive
      submitting={voidIt.isPending}
      disabled={!reason.trim()}
      onSubmit={() => voidIt.mutate()}
    >
      <Field label={t('violations.voidReason')} required>
        <Textarea value={reason} onChange={(e) => setReason(e.target.value)} autoFocus />
      </Field>
    </FormPage>
  );
}
